import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '../event';
import { DomAccessor } from '../browser';
import { OryNavigationResolver } from './ory-navigation-resolver';

/**
 * Minimal authentication context needed for logout navigation.
 * @remarks
 * Keeps LogoutHandler independent from Ory flow and consent request types.
 */
type LogoutContext = {
  oauth2_login_request?: { challenge?: string };
  return_to?: string;
  request_url?: string;
};

/**
 * Handles post-logout notification and navigation.
 *
 * @remarks
 * Centralizes post-logout behavior to avoid duplication across components
 * while keeping presentation logic in the presentation layer (shared/util).
 * Components call this after successful logout from their domain service.
 */
@Injectable({
  providedIn: 'root'
})
export class LogoutHandler {
  readonly #notificationService = inject(NotificationService);
  readonly #router = inject(Router);
  readonly #domAccessor = inject(DomAccessor);
  readonly #navigationResolver = inject(OryNavigationResolver);

  /**
   * Handles successful logout presentation.
   * Shows a success notification and chooses the next destination from the logout context.
   *
   * @param logoutContext - Optional authentication context for selecting the next destination.
   * Automatically extracts and processes:
   * - `login_challenge` from OAuth2 login request (prevents double reauthentication)
   * - `return_to` destination (filtered and sanitized based on URL classification)
   * - `request_url` from a consent request (restarts Hydra before creating a login flow)
   *
   * Reauthentication retains its pending login challenge. Consent logout restarts the
   * authorization request first, so Hydra can issue a fresh login challenge before login.
   *
   * @remarks
   * **Critical Behavior - URL Classification and Filtering:**
   *
   * Uses `OryNavigationResolver.classifyRedirect()` to determine which URLs are safe
   * to pass as `return_to`:
   *
   * **EXCLUDED (full-redirect type):**
   * - `/self-service/X?flow=...` - Ory API URLs with flow params (would fail Ory allowlist)
   * - Other full redirects (not suitable as Kratos `return_to` destinations)
   *
   * **INCLUDED (spa-navigation type):**
   * - `/self-service/X` → transformed to `/X` (Ory API URL without flow param)
   * - `/settings?flow=...` → sanitized to `/settings` (app route with stale flow param removed)
   * - `/settings` → kept as-is (clean app route)
   *
   * **Why this works:**
   * When logging out from reauthentication, the flow's `return_to` is typically
   * `/self-service/settings?flow=abc123`. This URL:
   * 1. Gets classified as 'full-redirect' (line 100-104 in OryNavigationResolver)
   * 2. Fails the `if (type === 'spa-navigation')` check
   * 3. Is NOT added to login query params
   * 4. Result: Login flow created WITHOUT the problematic return_to
   *
   * This prevents the Ory error: "return_to URL '/self-service/settings' is not allowed"
   *
   * @example
   * ```typescript
   * // Simple usage - handler filters and sanitizes automatically
   * this.#mfaService.logout()
   *   .subscribe({
   *     next: () => this.#logoutHandler.onSuccess(this.flow())
   *   });
   * ```
   */
  onSuccess(logoutContext?: LogoutContext): void {
    this.#notificationService.notifySuccess(
      'COMMON.NOTIFICATION.SUCCESS.LOGOUT',
      'COMMON.NOTIFICATION.SUCCESS.MESSAGE.LOGOUT'
    );

    if (logoutContext?.request_url) {
      // Restart Hydra first so its fresh login challenge is present when Kratos creates the flow.
      this.#navigationResolver.performRedirect(logoutContext.request_url);
      return;
    }

    void this.#router.navigate(['/login'], {
      queryParams: this.#getLoginQueryParams(logoutContext)
    });
  }

  #getLoginQueryParams(logoutContext?: LogoutContext): Record<string, string> | undefined {
    if (!logoutContext) {
      return undefined;
    }

    const params: Record<string, string> = {};

    // Extract OAuth context
    if (logoutContext.oauth2_login_request?.challenge) {
      params['login_challenge'] = logoutContext.oauth2_login_request.challenge;
    }

    // Filter and sanitize return_to based on URL classification
    if (logoutContext.return_to) {
      const returnTo = this.#getSafeReturnTo(logoutContext.return_to);
      if (returnTo) {
        params['return_to'] = returnTo;
      }
    }

    return Object.keys(params).length > 0 ? params : undefined;
  }

  #getSafeReturnTo(returnTo: string): string | undefined {
    // Classify URL to determine if it's safe for Ory's return_to allowlist
    // CRITICAL: /self-service/X?flow=... URLs are classified as 'full-redirect'
    // and excluded to prevent Ory allowlist errors
    const { type, spaRoute } = this.#navigationResolver.classifyRedirect(returnTo);

    if (type !== 'spa-navigation' || !spaRoute) {
      // If type === 'full-redirect', URL is excluded from return_to
      // (e.g., /self-service/X?flow=... or external URLs)
      return undefined;
    }

    // Only include spa-navigation URLs (safe for Ory allowlist)
    // Remove flow query parameter (becomes invalid after logout)
    return this.#removeFlowParameter(spaRoute);
  }

  #removeFlowParameter(spaRoute: string): string {
    try {
      const url = new URL(spaRoute, this.#domAccessor.window.location.origin);
      url.searchParams.delete('flow');
      return url.pathname + url.search;
    } catch {
      // If URL parsing fails, use spaRoute as-is
      return spaRoute;
    }
  }
}

